import classNames from 'classnames';
import clsx from 'clsx';
import React, { useState } from 'react';
import {
  TbArrowsExchange,
  TbCalendar,
  TbChevronLeft,
  TbChevronRight,
  TbLayoutSidebarLeftCollapseFilled,
  TbLayoutSidebarRightCollapseFilled
} from 'react-icons/tb';

import { Typography } from '@/components/atoms/Typography';

type Month = {
  value: string;
  label: string;
};

export const months: Month[] = Array.from({ length: 12 }, (_, i) => {
  const date = new Date();
  date.setMonth(date.getMonth() - (11 - i)); // pour que le mois le plus ancien soit en premier
  return {
    value: date.toISOString().slice(0, 7), // format YYYY-MM
    label: date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
  };
});

type Props = {
  startDate: Date | null;
  endDate: Date | null;
  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  months: Month[];
  maxVisibleMonths: number;
  selectedMonth: string;
  handleMonthSelection: (index: number) => void;
};

const MonthsDaysSelector: React.FC<Props> = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  currentIndex,
  setCurrentIndex,
  months,
  maxVisibleMonths,
  selectedMonth,
  handleMonthSelection
}) => {
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);

  const classDateInput = 'p-2 border rounded-md focus:ring-primary focus:border-primary';

  return (
    <div className="flex flex-col gap-8 mx-auto w-full justify-center items-center">
      <section className="md:hidden block">
        <div className="grid grid-cols-2 gap-5">
          <div className="flex flex-col items-center">
            <Typography variant="small" marginClassName="mb-2">
              Date de Début
            </Typography>
            <input
              type="date"
              value={startDate ? startDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
              className={classDateInput}
            />
          </div>
          <div className="flex flex-col items-center">
            <Typography variant="small" marginClassName="mb-2">
              Date de Fin
            </Typography>
            <input
              type="date"
              value={endDate ? endDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
              className={classDateInput}
            />
          </div>
        </div>
        <Typography align="center" marginClassName="mt-3">
          Consulter les stats entre <strong>{startDate?.toLocaleDateString()}</strong> et <strong>{endDate?.toLocaleDateString()}</strong>
        </Typography>
      </section>

      <section className="md:block hidden">
        <button
          onClick={() => setShowDatePicker(!showDatePicker)}
          className={clsx(
            'bg-gradient-to-l flex mx-auto items-center mb-5 text-xl gap-4 text-white px-4 py-2 rounded-md shadow hover:bg-primary/20',
            showDatePicker ? 'flex-row-reverse from-primary  to-shades-primary-active/70' : 'from-shades-primary-active/70  to-primary'
          )}
        >
          <div className="flex flex-col items-center gap-1">
            <span className="flex">
              <TbLayoutSidebarLeftCollapseFilled className="-mr-0.5" /> <TbLayoutSidebarRightCollapseFilled className="-ml-0.5" />
            </span>
            <Typography variant="extraSmall" color="white">
              Mensuel
            </Typography>
          </div>
          <TbArrowsExchange />
          <div className="flex flex-col items-center gap-1">
            <TbCalendar />
            <Typography variant="extraSmall" color="white">
              Calendrier
            </Typography>
          </div>
        </button>

        {showDatePicker ? (
          <>
            <div className="flex flex-row gap-10 justify-center">
              <div className="flex flex-col items-center">
                <Typography variant="small" marginClassName="mb-2">
                  Date de Début
                </Typography>
                <input
                  type="date"
                  value={startDate ? startDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                  className={classNames(classDateInput, 'w-92')}
                />
              </div>
              <div className="flex flex-col items-center">
                <Typography variant="small" marginClassName="mb-2">
                  Date de Fin
                </Typography>
                <input
                  type="date"
                  value={endDate ? endDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                  className={classNames(classDateInput, 'w-92')}
                />
              </div>
            </div>
            <Typography align="center" marginClassName="mt-5">
              Consulter les stats entre <strong>{startDate?.toLocaleDateString()}</strong> et <strong>{endDate?.toLocaleDateString()}</strong>
            </Typography>
          </>
        ) : (
          <div className="flex gap-32 items-center">
            <div className="flex items-center justify-center gap-5">
              <button
                onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                className="text-gray-500 hover:text-gray-700"
                disabled={currentIndex === 0}
              >
                <TbChevronLeft size={30} />
              </button>

              {/* Month Buttons */}
              <div className="flex gap-3 overflow-hidden">
                {months.slice(currentIndex, currentIndex + maxVisibleMonths).map((month, index) => (
                  <button
                    key={month.value}
                    onClick={() => handleMonthSelection(currentIndex + index)}
                    className={`pt-1 pb-1.5 w-24 rounded-md border-2 ${
                      selectedMonth === month.value ? 'bg-primary text-white border-primary' : 'bg-[#F0F0F0] text-gray-600 border-gray-300'
                    } hover:bg-primary hover:text-white transition-all duration-200`}
                  >
                    <h3 className="font-semibold text-md">{month.label.split(' ')[0]}</h3>
                    <p className="text-xs">{month.label.split(' ')[1]}</p>
                  </button>
                ))}
              </div>

              {/* Next Button */}
              <button
                onClick={() => setCurrentIndex((prev) => Math.min(months.length - maxVisibleMonths, prev + 1))}
                className="text-gray-500 hover:text-gray-700"
                disabled={currentIndex >= months.length - maxVisibleMonths}
              >
                <TbChevronRight size={30} />
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default MonthsDaysSelector;
